/**
 * Admin Users API Service
 *
 * Provides typed wrappers around the admin-user management endpoints.
 * Mirrors the Angular AdminService while adopting consistent error handling.
 */

import { httpClient } from './http-client.service';
import type {
  AdminApprovalPayload,
  AdminRole,
  AdminRolesResponse,
  AdminUser,
  AdminUserListResponse,
  BulkUploadUsersPayload,
  CreateAdminUserPayload,
  GetAdminUsersParams,
  KeyExistenceRequest,
  KeyExistenceResponse,
  SearchAdminUsersParams,
  UpdateAdminUserPayload,
  UpdateAdminUserStatusPayload,
} from '../types/admin-users';

/**
 * Response type for getUserById endpoint.
 * API returns user data directly in `data` field when fetching by user_id.
 */
interface GetUserByIdResponse {
  data: AdminUser | { rows: AdminUser[] };
}

class AdminUsersAPIService {
  /**
   * Fetch paginated admin users.
   */
  async getUsers(params: GetAdminUsersParams = {}): Promise<AdminUserListResponse> {
    const { signal, ...query } = params;
    const response = await httpClient.get<AdminUserListResponse>('userlist', {
      params: query,
      signal,
    });

    return response.data;
  }

  /**
   * Search users by a specific field/value pair.
   */
  async searchUsers(params: SearchAdminUsersParams): Promise<AdminUserListResponse> {
    const { field, value, signal } = params;
    const response = await httpClient.get<AdminUserListResponse>('userlist', {
      params: { [field]: value },
      signal,
    });

    return response.data;
  }

  /**
   * Fetch all users (used for CSV export).
   */
  async getAllUsers(): Promise<AdminUserListResponse> {
    const response = await httpClient.get<AdminUserListResponse>('userlist', {
      params: { all: true },
    });
    return response.data;
  }

  /**
   * Fetch a single user by ID. Returns null if not found.
   * Note: API returns user directly in data, not wrapped in rows array
   */
  async getUserById(userId: string): Promise<AdminUser | null> {
    const response = await httpClient.get<GetUserByIdResponse>('userlist', {
      params: { user_id: userId },
    });

    // API returns user directly in data (not in rows array) when fetching by user_id
    const userData = response.data?.data;

    // Check if it's a direct user object (has user_profile) or rows array format
    if (userData && 'user_profile' in userData) {
      return userData;
    }

    // Fallback: check if it's in rows array format
    if (userData && 'rows' in userData) {
      const rows = userData.rows ?? [];
      return rows.length > 0 ? rows[0] : null;
    }

    return null;
  }

  /**
   * Create a new admin user.
   */
  async createUser(payload: CreateAdminUserPayload): Promise<any> {
    const response = await httpClient.post('user/register', payload);
    return response.data;
  }

  /**
   * Update an existing admin user.
   */
  async updateUser(payload: UpdateAdminUserPayload): Promise<any> {
    const response = await httpClient.post('update/user', payload);
    return response.data;
  }

  /**
   * Delete a user by ID.
   */
  async deleteUser(userId: string): Promise<any> {
    const response = await httpClient.post('del/user', {
      params: { user_id: userId },
    });
    return response.data;
  }

  /**
   * Unlock a locked user.
   */
  async unlockUser(userId: string): Promise<any> {
    const response = await httpClient.post('update/unlockuser', {
      user_id: userId,
    });
    return response.data;
  }

  /**
   * Change user status (Activate / Deactivate).
   */
  async updateUserStatus(payload: UpdateAdminUserStatusPayload): Promise<any> {
    const response = await httpClient.post('update/updateuserstatus', payload);
    return response.data;
  }

  /**
   * Reset password for a user (matching Angular implementation).
   * Calls reset_password endpoint with type, email, and password.
   */
  async resetPassword(payload: { type: string; email: string; password: string }): Promise<any> {
    const response = await httpClient.post('reset_password', payload);
    return response.data;
  }

  /**
   * Check field existence to prevent duplicates (email/mobile/emp_id).
   */
  async checkKeyExistence(payload: KeyExistenceRequest): Promise<KeyExistenceResponse> {
    const response = await httpClient.post<KeyExistenceResponse>('keyexistance', payload);
    return response.data;
  }

  /**
   * Upload users in bulk from validated CSV rows.
   */
  async uploadBulkUsers(payload: BulkUploadUsersPayload): Promise<any> {
    const response = await httpClient.post('user/bulkRegister', payload);
    return response.data;
  }

  /**
   * Fetch available admin roles.
   */
  async getRoles(): Promise<AdminRole[]> {
    const response = await httpClient.post<AdminRolesResponse>('user/role/list', {});
    return response.data?.data ?? [];
  }

  /**
   * Approve a pending admin user.
   */
  async approveUser(payload: AdminApprovalPayload): Promise<any> {
    const response = await httpClient.post('update/user/approve', payload);
    return response.data;
  }

  /**
   * Reject a pending admin user.
   */
  async rejectUser(payload: AdminApprovalPayload): Promise<any> {
    const response = await httpClient.post('update/user/reject', payload);
    return response.data;
  }
}

export const adminUsersAPI = new AdminUsersAPIService();
export { AdminUsersAPIService };
